import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { BASE_URL } from "../api";

const API_BASE = `${BASE_URL}/api`;

/* ─── Global Styles ─────────────────────────────────────────────── */
const GlobalStyle = () => {
  useEffect(() => {
    const id = "billing-global-style";
    if (document.getElementById(id)) return;
    const el = document.createElement("style");
    el.id = id;
    el.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

      .billing-root {
        font-family: 'Sora', sans-serif;
        background: #F8FAFD;
        min-height: 100vh;
        padding: 36px 28px;
        color: #0D1F3C;
      }

      /* ─── Header ─── */
      .billing-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 28px;
        gap: 14px;
        flex-wrap: wrap;
      }
      .billing-title {
        font-size: 26px;
        font-weight: 700;
        letter-spacing: -0.5px;
        color: #0D1F3C;
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .billing-title-dot {
        width: 10px; height: 10px;
        border-radius: 50%;
        background: #3B82F6;
        display: inline-block;
      }

      .billing-controls {
        display: flex;
        gap: 10px;
        align-items: center;
        flex-wrap: wrap;
      }

      .billing-select {
        border: 1.5px solid #E2E8F0;
        border-radius: 8px;
        padding: 9px 14px;
        font-family: 'Sora', sans-serif;
        font-size: 14px;
        color: #0D1F3C;
        background: #fff;
        outline: none;
        cursor: pointer;
        transition: border-color 0.2s;
        min-width: 200px;
      }
      .billing-select:focus { border-color: #3B82F6; }

      /* ─── Buttons ─── */
      .btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 9px 18px;
        border: none;
        border-radius: 8px;
        font-family: 'Sora', sans-serif;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        transition: filter 0.15s, transform 0.1s;
        white-space: nowrap;
        line-height: 1;
      }
      .btn:disabled { opacity: 0.5; cursor: not-allowed; }
      .btn:not(:disabled):hover { filter: brightness(1.1); }
      .btn:not(:disabled):active { transform: scale(0.97); }

      .btn-primary  { background: #1A4F8A; color: #fff; }
      .btn-green    { background: #16A34A; color: #fff; }
      .btn-outline  { background: #fff; color: #1A4F8A; border: 1.5px solid #1A4F8A; }
      .btn-ghost    { background: #F1F5F9; color: #0D1F3C; border: 1.5px solid #E2E8F0; }
      .btn-sm       { padding: 6px 12px; font-size: 12px; }

      /* ─── Stat Cards ─── */
      .stats-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 18px;
        margin-bottom: 28px;
      }
      .stat-card {
        background: #fff;
        border-radius: 12px;
        padding: 20px 22px;
        box-shadow: 0 1px 4px rgba(13,31,60,0.07);
        border: 1px solid #E2E8F0;
        position: relative;
        overflow: hidden;
      }
      .stat-card::before {
        content: '';
        position: absolute;
        left: 0; top: 0; bottom: 0;
        width: 4px;
        background: var(--accent);
        border-radius: 0 2px 2px 0;
      }
      .stat-card-value {
        font-size: 26px;
        font-weight: 700;
        color: #0D1F3C;
        font-family: 'DM Mono', monospace;
        margin-bottom: 4px;
        line-height: 1.1;
      }
      .stat-card-label {
        font-size: 12px;
        font-weight: 600;
        color: #64748B;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      .stat-card-icon {
        position: absolute;
        right: 18px; top: 18px;
        font-size: 22px;
        opacity: 0.14;
      }

      /* ─── Table Card ─── */
      .table-card {
        background: #fff;
        border-radius: 12px;
        box-shadow: 0 4px 24px rgba(13,31,60,0.09);
        border: 1px solid #E2E8F0;
        overflow: hidden;
      }

      .table-card-header {
        padding: 16px 20px;
        border-bottom: 1px solid #E2E8F0;
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: #FAFBFD;
      }
      .table-card-title {
        font-size: 14px;
        font-weight: 700;
        color: #0D1F3C;
        letter-spacing: -0.2px;
      }
      .table-card-sub {
        font-size: 12px;
        color: #64748B;
        margin-top: 2px;
      }

      .billing-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 14px;
      }
      .billing-table thead {
        background: #0D1F3C;
        color: #fff;
      }
      .billing-table thead th {
        padding: 13px 18px;
        text-align: left;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.6px;
        white-space: nowrap;
      }
      .billing-table tbody tr {
        border-bottom: 1px solid #E2E8F0;
        transition: background 0.15s;
      }
      .billing-table tbody tr:last-child { border-bottom: none; }
      .billing-table tbody tr:hover { background: #F8FAFD; }
      .billing-table tbody td {
        padding: 14px 18px;
        vertical-align: middle;
      }

      .version-chip {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        background: #EFF6FF;
        color: #1A4F8A;
        border: 1px solid #BFDBFE;
        border-radius: 20px;
        padding: 3px 10px;
        font-size: 12px;
        font-weight: 700;
        font-family: 'DM Mono', monospace;
      }

      .amount-cell {
        font-family: 'DM Mono', monospace;
        font-weight: 600;
        font-size: 14px;
        color: #0D1F3C;
      }

      .date-cell {
        font-family: 'DM Mono', monospace;
        font-size: 12px;
        color: #64748B;
      }

      /* ─── Badges ─── */
      .badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        border-radius: 20px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.2px;
      }
      .badge-final    { background: #DCFCE7; color: #16A34A; }
      .badge-draft    { background: #FEF3C7; color: #D97706; }
      .badge-approved { background: #DCFCE7; color: #16A34A; }
      .badge-void     { background: #FEE2E2; color: #DC2626; }
      .badge-dot {
        width: 5px; height: 5px;
        border-radius: 50%;
        background: currentColor;
      }

      /* ─── Empty / Loading ─── */
      .empty-state {
        text-align: center;
        padding: 64px 20px;
        color: #64748B;
      }
      .empty-state-icon { font-size: 42px; margin-bottom: 12px; }
      .empty-state-text { font-size: 15px; font-weight: 500; }
      .empty-state-sub  { font-size: 13px; color: #94A3B8; margin-top: 4px; }

      /* ─── Toast ─── */
      .toast {
        position: fixed;
        right: 24px; top: 24px;
        z-index: 9999;
        background: #0D1F3C;
        color: #fff;
        padding: 14px 20px;
        border-radius: 12px;
        font-size: 14px;
        font-weight: 500;
        box-shadow: 0 8px 32px rgba(13,31,60,0.22);
        display: flex;
        align-items: center;
        gap: 8px;
        max-width: 320px;
        animation: toastIn 0.25s ease;
      }
      @keyframes toastIn {
        from { opacity: 0; transform: translateX(30px); }
        to   { opacity: 1; transform: translateX(0); }
      }

      /* ─── Modal ─── */
      .modal-overlay {
        position: fixed;
        inset: 0;
        background: rgba(13,31,60,0.44);
        backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        padding: 20px;
        animation: fadeIn 0.2s ease;
      }
      @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

      .modal-box {
        background: #fff;
        border-radius: 14px;
        box-shadow: 0 24px 64px rgba(13,31,60,0.22);
        width: 100%;
        max-width: 860px;
        max-height: 92vh;
        overflow-y: auto;
        animation: popIn 0.22s cubic-bezier(0.34,1.56,0.64,1);
      }
      @keyframes popIn {
        from { opacity: 0; transform: scale(0.93); }
        to   { opacity: 1; transform: scale(1); }
      }

      .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 20px 24px 16px;
        border-bottom: 1px solid #E2E8F0;
        position: sticky;
        top: 0;
        background: #fff;
        z-index: 2;
      }
      .modal-title {
        font-size: 18px;
        font-weight: 700;
        color: #0D1F3C;
      }
      .modal-subtitle {
        font-size: 12px;
        color: #64748B;
        margin-top: 2px;
        font-family: 'DM Mono', monospace;
      }
      .modal-close {
        width: 32px; height: 32px;
        border-radius: 8px;
        border: none;
        background: #F1F5F9;
        cursor: pointer;
        font-size: 18px;
        display: flex; align-items: center; justify-content: center;
        color: #64748B;
        transition: background 0.15s;
      }
      .modal-close:hover { background: #E2E8F0; }

      .modal-body { padding: 24px; }

      .modal-footer {
        padding: 16px 24px;
        border-top: 1px solid #E2E8F0;
        display: flex;
        gap: 10px;
        justify-content: flex-end;
        background: #FAFBFD;
      }

      /* ─── Modal table ─── */
      .modal-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 14px;
        border-radius: 10px;
        overflow: hidden;
        border: 1px solid #E2E8F0;
      }
      .modal-table thead {
        background: #0D1F3C;
        color: #fff;
      }
      .modal-table thead th {
        padding: 11px 16px;
        text-align: left;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      .modal-table tbody tr {
        border-bottom: 1px solid #E2E8F0;
        transition: background 0.15s;
      }
      .modal-table tbody tr:last-child { border-bottom: none; }
      .modal-table tbody tr:hover { background: #F8FAFD; }
      .modal-table tbody td {
        padding: 11px 16px;
        vertical-align: middle;
      }

      /* ─── Total Banner ─── */
      .total-banner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: linear-gradient(135deg, #0D1F3C 0%, #1A4F8A 100%);
        color: #fff;
        border-radius: 12px;
        padding: 20px 24px;
        margin-top: 20px;
      }
      .total-banner-label { font-size: 13px; opacity: 0.7; font-weight: 500; }
      .total-banner-amount {
        font-size: 30px;
        font-weight: 700;
        font-family: 'DM Mono', monospace;
      }

      /* ─── Bill detail meta ─── */
      .bill-meta {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 14px;
        margin-bottom: 20px;
      }
      .bill-meta-item {
        background: #F8FAFD;
        border: 1px solid #E2E8F0;
        border-radius: 10px;
        padding: 14px 16px;
      }
      .bill-meta-label {
        font-size: 11px;
        font-weight: 600;
        color: #64748B;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        margin-bottom: 4px;
      }
      .bill-meta-value {
        font-size: 15px;
        font-weight: 700;
        color: #0D1F3C;
        font-family: 'DM Mono', monospace;
      }

      /* ─── Scrollbar ─── */
      .modal-box::-webkit-scrollbar { width: 6px; }
      .modal-box::-webkit-scrollbar-track { background: transparent; }
      .modal-box::-webkit-scrollbar-thumb { background: #E2E8F0; border-radius: 4px; }

      @media (max-width: 760px) {
        .stats-grid { grid-template-columns: repeat(2, 1fr); }
        .billing-header { flex-direction: column; align-items: flex-start; }
        .billing-root { padding: 16px; }
        .bill-meta { grid-template-columns: 1fr 1fr; }
      }
      @media (max-width: 480px) {
        .stats-grid { grid-template-columns: 1fr 1fr; }
        .bill-meta { grid-template-columns: 1fr; }
      }
    `;
    document.head.appendChild(el);
  }, []);
  return null;
};

/* ─── API Layer ─────────────────────────────────────────────────── */
const BillingAPI = {
  async getCamps() {
    const res = await fetch(`${API_BASE}/camps/`);
    if (!res.ok) throw new Error("Failed to load camps");
    return res.json();
  },
  async getCampBills(campId) {
    const res = await fetch(`${API_BASE}/camp-bills/${campId}/`);
    if (!res.ok) throw new Error("Failed to load bills");
    return res.json();
  },
  async previewBill(campId) {
    const res = await fetch(`${API_BASE}/preview-bill/${campId}/`);
    if (!res.ok) throw new Error("Preview failed");
    return res.json();
  },
  async generateBill(campId) {
    const res = await fetch(`${API_BASE}/generate-bill/${campId}/`, { method: "POST" });
    if (!res.ok) throw new Error("Generation failed");
    return res.json();
  },
  async getBill(id) {
    const res = await fetch(`${API_BASE}/bill/${id}/`);
    if (!res.ok) throw new Error("Failed to load bill");
    return res.json();
  },
};

/* ─── Reusable Modal ─────────────────────────────────────────────── */
const Modal = ({ title, subtitle, onClose, children, footer }) => (
  <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
    <div className="modal-box">
      <div className="modal-header">
        <div>
          <div className="modal-title">{title}</div>
          {subtitle && <div className="modal-subtitle">{subtitle}</div>}
        </div>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      <div className="modal-body">{children}</div>
      {footer && <div className="modal-footer">{footer}</div>}
    </div>
  </div>
);

/* ─── Badge ─────────────────────────────────────────────────────── */
const Badge = ({ status }) => {
  const map = {
    Final: "badge-final", Draft: "badge-draft",
    Approved: "badge-approved", Void: "badge-void",
  };
  return (
    <span className={`badge ${map[status] || "badge-draft"}`}>
      <span className="badge-dot" />
      {status}
    </span>
  );
};

/* ─── Stat Card ─────────────────────────────────────────────────── */
const StatCard = ({ label, value, accent, icon }) => (
  <div className="stat-card" style={{ "--accent": accent }}>
    <span className="stat-card-icon">{icon}</span>
    <div className="stat-card-value">{value}</div>
    <div className="stat-card-label">{label}</div>
  </div>
);

/* ─── Preview Modal ─────────────────────────────────────────────── */
const PreviewModal = ({ preview, onClose, onGenerate }) => {
  if (!preview) return null;
  return (
    <Modal
      title="Bill Preview"
      subtitle="Review line items before generating"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-green" onClick={onGenerate}>Generate Bill</button>
        </>
      }
    >
      <table className="modal-table">
        <thead>
          <tr>
            <th>Service</th>
            <th>Actual Cases</th>
            <th>Billable Cases</th>
            <th>Rate (₹)</th>
            <th>Amount (₹)</th>
          </tr>
        </thead>
        <tbody>
          {(preview.preview || []).map((x, i) => (
            <tr key={i}>
              <td style={{ fontWeight: 500 }}>{x.service_name}</td>
              <td style={{ fontFamily: "'DM Mono', monospace" }}>{x.actual_cases}</td>
              <td style={{ fontFamily: "'DM Mono', monospace" }}>{x.billable_cases}</td>
              <td style={{ fontFamily: "'DM Mono', monospace" }}>{x.rate}</td>
              <td style={{ fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>
                ₹{Number(x.amount).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="total-banner">
        <span className="total-banner-label">Grand Total</span>
        <span className="total-banner-amount">₹{Number(preview.total_amount).toLocaleString()}</span>
      </div>
    </Modal>
  );
};

/* ─── Bill Detail Modal ─────────────────────────────────────────── */
const BillDetailModal = ({ bill, onClose }) => {
  const handleDownload = () => {
      if (!bill?.pdf_file) return;

      const pdfUrl = bill.pdf_file.replace("http://", "https://");

      const a = document.createElement("a");
      a.href = pdfUrl;
      a.download = `${bill.invoice_number}.pdf`;
      a.target = "_blank";

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
  };

  if (!bill) return null;
  return (
    <Modal
      title={`Bill #${bill.id}`}
      subtitle={`Version ${bill.version} · ${bill.is_latest ? "Final" : "Draft"}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          <button
            className="btn btn-primary"
            onClick={handleDownload}
          >
            ⬇ Download PDF
          </button>
        </>
      }
    >
      <div className="bill-meta">
        <div className="bill-meta-item">
          <div className="bill-meta-label">Total Amount</div>
          <div className="bill-meta-value" style={{ color: "#1A4F8A" }}>
            ₹{Number(bill.total_amount).toLocaleString()}
          </div>
        </div>
        <div className="bill-meta-item">
          <div className="bill-meta-label">Version</div>
          <div className="bill-meta-value">v{bill.version}</div>
        </div>
        <div className="bill-meta-item">
          <div className="bill-meta-label">Status</div>
          <div style={{ marginTop: 4 }}>
            <Badge status={bill.is_latest ? "Final" : "Draft"} />
          </div>
        </div>
      </div>

      <table className="modal-table">
        <thead>
          <tr>
            <th>Service</th>
            <th>Actual Cases</th>
            <th>Billable Cases</th>
            <th>Rate (₹)</th>
            <th>Amount (₹)</th>
          </tr>
        </thead>
        <tbody>
          {(bill.details || []).map(d => (
            <tr key={d.id}>
              <td style={{ fontWeight: 500 }}>{d.service_name}</td>
              <td style={{ fontFamily: "'DM Mono', monospace" }}>{d.total_cases}</td>
              <td style={{ fontFamily: "'DM Mono', monospace" }}>{d.billable_cases}</td>
              <td style={{ fontFamily: "'DM Mono', monospace" }}>{d.rate}</td>
              <td style={{ fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>
                ₹{Number(d.amount).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="total-banner" style={{ marginTop: 20 }}>
        <span className="total-banner-label">Bill Total</span>
        <span className="total-banner-amount">₹{Number(bill.total_amount).toLocaleString()}</span>
      </div>
    </Modal>
  );
};

/* ─── Main Billing Page ─────────────────────────────────────────── */
export default function Billing() {
  const [camps, setCamps] = useState([]);
  const [selectedCamp, setSelectedCamp] = useState("");
  const [bills, setBills] = useState([]);
  const [preview, setPreview] = useState(null);
  const [selectedBill, setSelectedBill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const notify = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3200);
  };

  useEffect(() => { loadCamps(); }, []);

  const loadCamps = async () => {
    try {
      const data = await BillingAPI.getCamps();
      setCamps(data);
      if (data.length) {
        setSelectedCamp(data[0].id);
        loadBills(data[0].id);
      }
    } catch { notify("Failed to load camps"); }
  };

  const loadBills = async (id) => {
    try {
      setLoading(true);
      const data = await BillingAPI.getCampBills(id);
      setBills(data);
    } catch { notify("Failed to load bills"); }
    finally { setLoading(false); }
  };

  const handlePreview = async () => {
    try {
      const data = await BillingAPI.previewBill(selectedCamp);
      setPreview(data);
    } catch { notify("Preview failed"); }
  };

  const handleGenerate = async () => {
    try {
      await BillingAPI.generateBill(selectedCamp);
      notify("✓ Bill generated successfully");
      setPreview(null);
      loadBills(selectedCamp);
    } catch { notify("Bill generation failed"); }
  };

  const openBill = async (id) => {
    try {
      const data = await BillingAPI.getBill(id);
      setSelectedBill(data);
    } catch { notify("Failed to load bill details"); }
  };

  const stats = useMemo(() => ({
    count: bills.length,
    amount: bills.reduce((a, b) => a + Number(b.total_amount || 0), 0),
    versions: bills.length,
    latest: bills?.[0]?.version || "—",
  }), [bills]);

  const selectedCampName = camps.find(c => String(c.id) === String(selectedCamp))?.branch_name || "";

  return (
    <>
      <GlobalStyle />
      <div className="billing-root">

        {/* Toast */}
        {toast && <div className="toast">{toast}</div>}

        {/* Header */}
        <div className="billing-header">
          <h1 className="billing-title">
            <span className="billing-title-dot" />
            Billing
          </h1>
          <div className="billing-controls">
            <Select
              className="billing-select"
              placeholder="Search Camp..."
              isSearchable
              options={camps.map(c => ({
                value: c.id,
                label: c.branch_name,
              }))}
              value={
                camps
                  .map(c => ({
                    value: c.id,
                    label: c.branch_name,
                  }))
                  .find(c => c.value === selectedCamp)
              }
              onChange={(option) => {
                setSelectedCamp(option.value);
                loadBills(option.value);
              }}
            />
            <button className="btn btn-outline" onClick={handlePreview}>
              Preview Bill
            </button>
            <button className="btn btn-primary" onClick={handleGenerate}>
              Generate Bill
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="stats-grid">
          <StatCard label="Total Bills" value={stats.count} accent="#1A4F8A" icon="🧾" />
          <StatCard label="Total Revenue" value={`₹${stats.amount.toLocaleString()}`} accent="#16A34A" icon="💰" />
          <StatCard label="Versions" value={stats.versions} accent="#D97706" icon="📋" />
          <StatCard label="Latest Version" value={`v${stats.latest}`} accent="#7C3AED" icon="✦" />
        </div>

        {/* Table Card */}
        <div className="table-card">
          <div className="table-card-header">
            <div>
              <div className="table-card-title">Bill History</div>
              {selectedCampName && (
                <div className="table-card-sub">{selectedCampName}</div>
              )}
            </div>
            <span style={{
              fontSize: 12, fontWeight: 600, color: "#64748B",
              background: "#F1F5F9", padding: "4px 10px", borderRadius: 20,
              border: "1px solid #E2E8F0"
            }}>
              {bills.length} record{bills.length !== 1 ? "s" : ""}
            </span>
          </div>

          <table className="billing-table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Total Amount</th>
                <th>Status</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5}>
                    <div className="empty-state">
                      <div className="empty-state-text">Loading bills…</div>
                    </div>
                  </td>
                </tr>
              ) : bills.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <div className="empty-state">
                      <div className="empty-state-icon">🧾</div>
                      <div className="empty-state-text">No bills generated yet</div>
                      <div className="empty-state-sub">Use "Generate Bill" to create the first version</div>
                    </div>
                  </td>
                </tr>
              ) : bills.map(b => (
                <tr key={b.id}>
                  <td>
                    <span className="version-chip">v{b.version}</span>
                  </td>
                  <td className="amount-cell">
                    ₹{Number(b.total_amount).toLocaleString()}
                  </td>
                  <td>
                    <Badge status={b.is_latest ? "Final" : "Draft"} />
                  </td>
                  <td className="date-cell">
                    {new Date(b.created_at || Date.now()).toLocaleDateString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric"
                    })}
                  </td>
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={() => openBill(b.id)}>
                      View →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Modals */}
        <PreviewModal
          preview={preview}
          onClose={() => setPreview(null)}
          onGenerate={handleGenerate}
        />
        <BillDetailModal
          bill={selectedBill}
          onClose={() => setSelectedBill(null)}
        />

      </div>
    </>
  );
}